import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface WaterBodiesFiltersState {
  nameSearch: string;
  codeSearch: string;
  selectedTypes: FilterIdsType;
  selectedSubtypes: FilterIdsType;
  selectedSeasonStarts: FilterIdsType;
  selectedSeasonEnds: FilterIdsType;
  hasSelectableToggled: boolean;
  hasBoatAllowedToggled: boolean;
  hasClosedToggled: boolean;
}

const initialState: WaterBodiesFiltersState = {
  nameSearch: '',
  codeSearch: '',
  selectedTypes: [],
  selectedSubtypes: [],
  selectedSeasonStarts: [],
  selectedSeasonEnds: [],
  hasSelectableToggled: false,
  hasBoatAllowedToggled: false,
  hasClosedToggled: false,
};

const waterBodiesFiltersSlice = createSlice({
  name: 'waterBodiesFilters',
  initialState,
  reducers: {
    setNameSearch(state, action: PayloadAction<string>) {
      state.nameSearch = action.payload;
    },
    setCodeSearch(state, action: PayloadAction<string>) {
      state.codeSearch = action.payload;
    },
    setSelectedTypes(state, action: PayloadAction<FilterIdsType>) {
      state.selectedTypes = action.payload;
    },
    setSelectedSubtypes(state, action: PayloadAction<FilterIdsType>) {
      state.selectedSubtypes = action.payload;
    },
    setSelectedSeasonStarts(state, action: PayloadAction<FilterIdsType>) {
      state.selectedSeasonStarts = action.payload;
    },
    setSelectedSeasonEnds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedSeasonEnds = action.payload;
    },
    setHasSelectableToggled(state, action: PayloadAction<boolean>) {
      state.hasSelectableToggled = action.payload;
    },
    setHasBoatAllowedToggled(state, action: PayloadAction<boolean>) {
      state.hasBoatAllowedToggled = action.payload;
    },
    setHasClosedToggled(state, action: PayloadAction<boolean>) {
      state.hasClosedToggled = action.payload;
    },
    resetFilters(state) {
      state.nameSearch = initialState.nameSearch;
      state.codeSearch = initialState.codeSearch;
      state.selectedTypes = initialState.selectedTypes;
      state.selectedSubtypes = initialState.selectedSubtypes;
      state.selectedSeasonStarts = initialState.selectedSeasonStarts;
      state.selectedSeasonEnds = initialState.selectedSeasonEnds;
    },
    resetQuickFilters(state) {
      state.hasSelectableToggled = initialState.hasSelectableToggled;
      state.hasBoatAllowedToggled = initialState.hasBoatAllowedToggled;
      state.hasClosedToggled = initialState.hasClosedToggled;
    },
  },
});

export const {
  setNameSearch,
  setCodeSearch,
  setSelectedTypes,
  setSelectedSubtypes,
  setSelectedSeasonStarts,
  setSelectedSeasonEnds,
  setHasSelectableToggled,
  setHasBoatAllowedToggled,
  setHasClosedToggled,
} = waterBodiesFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { waterBodiesFilters: WaterBodiesFiltersState }) => {
  const { nameSearch, codeSearch, selectedTypes, selectedSubtypes, selectedSeasonStarts, selectedSeasonEnds } =
    state.waterBodiesFilters;

  return (
    !!nameSearch ||
    !!codeSearch ||
    !!selectedTypes.length ||
    !!selectedSubtypes.length ||
    !!selectedSeasonStarts.length ||
    !!selectedSeasonEnds.length
  );
};

export const activeFiltersCountSelector = (state: { waterBodiesFilters: WaterBodiesFiltersState }) => {
  const {
    nameSearch,
    codeSearch,
    selectedTypes,
    selectedSubtypes,
    selectedSeasonStarts,
    selectedSeasonEnds,
    hasSelectableToggled,
    hasBoatAllowedToggled,
    hasClosedToggled,
  } = state.waterBodiesFilters;

  let count = 0;
  if (nameSearch) count++;
  if (codeSearch) count++;
  if (selectedTypes.length > 0) count++;
  if (selectedSubtypes.length > 0) count++;
  if (selectedSeasonStarts.length > 0) count++;
  if (selectedSeasonEnds.length > 0) count++;
  if (hasSelectableToggled) count++;
  if (hasBoatAllowedToggled) count++;
  if (hasClosedToggled) count++;

  return count;
};

export const areQuickFiltersAppliedSelector = (state: { waterBodiesFilters: WaterBodiesFiltersState }) => {
  const { hasSelectableToggled, hasBoatAllowedToggled, hasClosedToggled } = state.waterBodiesFilters;

  return hasSelectableToggled || hasBoatAllowedToggled || hasClosedToggled;
};

export const waterBodiesFiltersActions = waterBodiesFiltersSlice.actions;

export const waterBodiesFilters = waterBodiesFiltersSlice.reducer;
